import { t } from 'i18next';
import { useMutation } from 'react-query';

import { elementService } from 'bom/services';
import { IElement, IElementListRequestPayload, IUpsertElementPayload } from 'bom/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { IInfiniteQueryOptions } from 'core/types';

export const useGetElements = (
  requestPayload?: IElementListRequestPayload,
  options: IInfiniteQueryOptions<IElement> = {}
) => useInfiniteQuery<IElement>(elementService.getElements, 'elements', requestPayload, options);

export const useElementActions = ({
  notificationPosition = NotificationPosition.FORM,
}: {
  notificationPosition?: NotificationPosition;
} = {}) => ({
  ...useUpsertElement(notificationPosition),
  ...useUpsertElementComponentNote(),
  ...useRemoveElementComponentNotes(),
  ...useUpsertElementComponentNotes(),
  ...useSaveElementAsTemplate(),
});

const useUpsertElement = (notificationPosition = NotificationPosition.FORM) => {
  const { errors } = useHandleErrors({
    success: ({ element }) =>
      element.id
        ? { message: t('messageElementUpdateSuccess') }
        : { message: t('messageElementCreateSuccess') },
    loading: ({ element }) =>
      element.id
        ? { message: t('messageElementUpdateInProgress') }
        : { message: t('messageElementCreateInProgress') },
    error: ({ element }) =>
      element?.id
        ? { title: t('errorTitleElementUpdate') }
        : { title: t('errorTitleElementCreate') },
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(
    (data: IUpsertElementPayload) => {
      const { shouldAddTemplateToBom, handleAddTemplateToBom } = elementUpsertHelper();

      if (shouldAddTemplateToBom(data)) {
        return handleAddTemplateToBom(data);
      }

      return elementService.upsertElement(data);
    },
    {
      ...errors,
    }
  );

  return { upsertElement: mutateAsync };
};

const useUpsertElementComponentNote = () => {
  const { errors } = useHandleErrors({
    success: ({ elementComponentNote }) =>
      elementComponentNote.id
        ? { message: t('messageElementComponentNoteUpdateSuccess') }
        : { message: t('messageElementComponentNoteCreateSuccess') },
    loading: ({ elementComponentNote }) =>
      elementComponentNote.id
        ? { message: t('messageElementComponentNoteUpdateInProgress') }
        : { message: t('messageElementComponentNoteCreateInProgress') },
    error: ({ elementComponentNote }) =>
      elementComponentNote.id
        ? { title: t('errorTitleElementComponentNoteUpdate') }
        : { title: t('errorTitleElementComponentNoteCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(elementService.upsertElementComponentNote, {
    ...errors,
  });

  return { upsertElementComponentNote: mutateAsync };
};

const useRemoveElementComponentNotes = () => {
  const { errors } = useHandleErrors({
    success: ({ ids }) => ({
      message: t('messageElementComponentNotesRemoveSuccess', { count: ids.length }),
    }),
    loading: ({ ids }) => ({
      message: t('messageElementComponentNotesRemoveInProgress', { count: ids.length }),
    }),
    error: ({ ids }) => ({
      title: t('errorTitleElementComponentNotesRemove', { count: ids.length }),
    }),
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(elementService.removeElementComponentNotes, {
    ...errors,
  });

  return { removeElementComponentNotes: mutateAsync };
};

const useUpsertElementComponentNotes = () => {
  const { errors } = useHandleErrors({
    success: ({ elementComponentNotes }) =>
      elementComponentNotes.length > 0 && elementComponentNotes[0].id
        ? {
            message: t('messageElementComponentNotesUpdateSuccess', {
              count: elementComponentNotes.length,
            }),
          }
        : {
            message: t('messageElementComponentNotesCreateSuccess', {
              count: elementComponentNotes.length,
            }),
          },
    loading: ({ elementComponentNotes }) =>
      elementComponentNotes.length > 0 && elementComponentNotes[0].id
        ? {
            message: t('messageElementComponentNotesUpdateInProgress', {
              count: elementComponentNotes.length,
            }),
          }
        : {
            message: t('messageElementComponentNotesCreateInProgress', {
              count: elementComponentNotes.length,
            }),
          },
    error: ({ elementComponentNotes }) =>
      elementComponentNotes.length > 0 && elementComponentNotes[0].id
        ? {
            title: t('errorTitleElementComponentNotesUpdate', {
              count: elementComponentNotes.length,
            }),
          }
        : {
            title: t('errorTitleElementComponentNotesCreate', {
              count: elementComponentNotes.length,
            }),
          },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(elementService.upsertElementComponentNotes, {
    ...errors,
  });

  return { upsertElementComponentNotes: mutateAsync };
};

const useSaveElementAsTemplate = () => {
  const { errors } = useHandleErrors({
    loading: () => ({ message: t('messageElementSaveAsTemplateInProgress') }),
    error: () => ({ title: t('errorTitleElementSaveAsTemplate') }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(elementService.saveElementAsTemplate, {
    ...errors,
  });

  return { saveElementAsTemplate: mutateAsync };
};

const elementUpsertHelper = () => {
  const shouldAddTemplateToBom = (data: IUpsertElementPayload) =>
    !data.element.id && !!data.element.elementTemplateId;

  const createAddTemplateToBomPayload = (data: IUpsertElementPayload) => ({
    includeCost: data.includeCost,
    data: {
      bomId: data.element.bomId,
      elementTemplateId: data.element.elementTemplateId,
      name: data.element.name,
      quantity: data.element.quantity,
    },
  });

  const handleAddTemplateToBom = (data: IUpsertElementPayload) =>
    elementService.addElementTemplateToJob(createAddTemplateToBomPayload(data));

  return {
    shouldAddTemplateToBom,
    createAddTemplateToBomPayload,
    handleAddTemplateToBom,
  };
};
